import Flickity from 'flickity';
import capitalize from 'lodash/capitalize';
import forEach from 'lodash/forEach';

import analytics from './analytics';

function getIncomeRangeString(income) {
    if (income < 75000) return '< $75,000';
    if (income >= 75000 && income < 100000) return '$75,000 - $99,999';
    if (income >= 100000 && income < 125000) return '$100,000 - $124,999';
    if (income >= 125000 && income < 150000) return '$125,000 - $149,999';
    if (income >= 150000 && income < 300000) return '$150,000 - $299,999';
    if (income >= 300000 && income < 500000) return '$300,000 - $499,999';
    return '$500,000 +';
}

class CtaBlock {
    constructor(container) {
        // Initialise slider
        this.slider = new Flickity(container, {
            prevNextButtons: false,
            pageDots: false,
            draggable: false,
        });

        this.type = container.dataset.type;

        // Select panels
        this.panelStart = container.querySelector('[data-panel=start]');

        this.validationMessage = container.querySelector('.cta-block__validation');

        // Select fields
        this.inputAge = container.querySelector('[data-name=age]');
        this.inputBirthYear = container.querySelector('[data-name=birthYear]');
        this.inputIncome = container.querySelector('[data-name=income]');
        this.inputFund = container.querySelector('[data-name=fund]');
        this.tooltip = container.querySelector('.js-tooltip');
        this.riskAssessmentLink = container.querySelector('.js-risk-assessment');
        this.form = container.querySelector('form');

        this.addEventListeners();
    }

    addEventListeners() {
        this.panelStart.querySelector('button').addEventListener('click', (event) => {
            event.preventDefault();
            this.validate();
        });

        this.inputIncome.addEventListener('keyup', (event) => {
            if (this.inputIncome.value) {
                const ignoredKeyCodes = [37, 38, 39, 40];
                if (!ignoredKeyCodes.includes(event.keyCode)) {
                    this.inputIncome.value = this.inputIncome.value.replace(/,/gi, '').replace(/\d(?=(?:\d{3})+$)/g, '$&,');
                    this.inputIncome.style.width = 'auto';
                    this.inputIncome.size = this.inputIncome.value.length;
                }
            } else {
                this.inputIncome.size = 6;
            }
        });

        this.inputIncome.addEventListener('blur', () => {
            this.toggleTooltip();
        });

        this.inputIncome.addEventListener('focus', () => {
            this.toggleTooltip();
        });
    }

    toggleTooltip() {
        if (this.tooltip) {
            this.tooltip.classList.toggle('is-visible');
        }
    }

    validate() {
        if (this.inputIncome.value && this.inputAge.value) {
            if (Number(this.inputAge.value) < 18) {
                this.inputAge.parentNode.classList.add('is-invalid');
                this.showErrorMessage('You must be over the age of 18');
            } else if (Number(this.inputIncome.value.replace(/,/gi, '')) < 5000) {
                this.inputIncome.parentNode.classList.add('is-invalid');
                this.showErrorMessage('Please contact Six Park to discuss your investment options.');
            } else if (this.inputFund.value.length === 0) {
                this.inputFund.parentNode.classList.add('is-invalid');
                this.showErrorMessage('Please select the funds used for this investment.');
            } else {
                this.handleNext();
            }
        } else {
            this.inputAge.parentNode.classList.add('is-invalid');
            this.inputIncome.parentNode.classList.add('is-invalid');
            this.showErrorMessage();
        }
    }

    showErrorMessage(message = 'Please complete all the fields above') {
        this.validationMessage.textContent = message;
        this.validationMessage.classList.add('is-visible');
    }

    handleNext() {
        switch (this.type) {
        case 'getStarted':
            this.appendRiskAssessmentUrlParams();

            analytics.captureGetStarted(
                this.inputAge.value,
                this.inputIncome.value.replace(/,/gi, ''),
                capitalize(this.inputFund.value),
            );

            if (this.inputFund.value === 'super') {
                this.slider.next();
            } else {
                this.riskAssessmentLink.click();
            }
            break;
        case 'enquire':
            this.submitEnquiry();
            break;
        default:
        }
    }

    async submitEnquiry() {
        try {
            // Convert age to year of birth
            const currentYear = new Date().getFullYear();
            this.inputBirthYear.value = currentYear - this.inputAge.value;

            const response = await fetch('/', {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                credentials: 'same-origin',
                body: new FormData(this.form),
            });

            const json = await response.json();

            if (json.success) {
                this.slider.next();

                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                    event: 'enquiryStep',
                    superBalance: `${getIncomeRangeString(Number(this.inputIncome.value.replace(/,/gi, '')))}`,
                });
            } else {
                forEach(json.errors, (errorMessage, errorName) => {
                    this.form.querySelector(`[data-name=${errorName}]`).parentNode.classList.add('is-invalid');
                    this.showErrorMessage(errorMessage);
                });
            }

        } catch (err) {
            window.bugsnagClient.notify(err);
            this.showErrorMessage('Oops, an error has occurred, please refresh the page and try again or try again later.');
        }
    }

    appendRiskAssessmentUrlParams() {
        const inputAge = Number(this.inputAge.value);
        const inputIncome = Number(this.inputIncome.value.replace(/,/gi, ''));
        const inputFunds = this.inputFund.value;

        const ageParam = (age) => {
            if (age >= 18 && age <= 25) return 1;
            if (age >= 26 && age <= 35) return 2;
            if (age >= 36 && age <= 50) return 3;
            return 4;
        };

        const investmentParam = (investment) => {
            if (investment < 75000) return '13';
            if (investment >= 75000 && investment < 100000) return '14';
            if (investment >= 100000 && investment < 125000) return '15';
            if (investment >= 125000 && investment < 150000) return '16';
            if (investment >= 150000 && investment < 300000) return '17';
            if (investment >= 300000 && investment < 500000) return '18';
            return '19';
        };

        this.riskAssessmentLink.href += `?age=${ageParam(inputAge)}&investment=${investmentParam(inputIncome)}&funds=${inputFunds}`;
    }
}

const containers = Array.from(document.querySelectorAll('.js-cta-block'));
containers.forEach(container => new CtaBlock(container));



// WEBPACK FOOTER //
// ./app/scripts/components/cta-block.js